import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import { groupBy } from 'lodash';
import { useMemo, useState } from 'react';

import {
  ClientTeamMember,
  Hotel,
  RosterRoomAssignment,
} from '../../generated/graphql';
import buildClientTeamMemberDisplayName from '../../utilities/buildClientTeamMemberDisplayName';
import { Button } from '../Button';

type Props = {
  rosterRoomAssignments: Array<
    Pick<RosterRoomAssignment, 'id'> & {
      hotel: Pick<Hotel, 'name'>;
      guest: Pick<
        ClientTeamMember,
        'id' | 'firstName' | 'lastName' | 'semiUniqueIdentifier'
      >;
    }
  >;
  removeAssignment?: (id: string) => void;
};

export default function RosterBulkEditDetails({
  rosterRoomAssignments,
  removeAssignment,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  const groupedRosterEntries = useMemo(() => {
    return groupBy(rosterRoomAssignments, 'hotel.name');
  }, [rosterRoomAssignments]);

  const assignmentsCount = rosterRoomAssignments.length;

  if (assignmentsCount < 1) {
    return null;
  }

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="rounded-md border border-gray-300 bg-white p-4">
      <button
        onClick={toggleExpand}
        className="flex w-full items-center justify-between"
      >
        <h2 className="text-lg">Selected {assignmentsCount} roster entries</h2>
        <span>
          {isExpanded ? (
            <ChevronUpIcon className="size-5" aria-hidden="true" />
          ) : (
            <ChevronDownIcon className="size-5" aria-hidden="true" />
          )}
        </span>
      </button>
      {isExpanded && (
        <div>
          {groupedRosterEntries && (
            <>
              {Object.entries(groupedRosterEntries).map(
                ([hotelName, rosterEntries]) => {
                  return (
                    <div
                      key={hotelName}
                      className="mb-5 flex flex-col gap-5 pb-2 pt-6"
                    >
                      <h4 className="pb-2 text-sm font-medium">{hotelName}</h4>
                      <ol className="flex list-inside list-decimal flex-wrap gap-6">
                        {rosterEntries.map(
                          (
                            rosterEntry: NonNullable<
                              Props['rosterRoomAssignments']
                            >[number],
                          ) => {
                            if (!rosterEntry) {
                              return null;
                            }
                            return (
                              <li
                                key={rosterEntry.id}
                                className="flex flex-row items-center"
                              >
                                {buildClientTeamMemberDisplayName(
                                  rosterEntry.guest,
                                )}{' '}
                                {removeAssignment && (
                                  <Button
                                    className="ml-2 border-0 bg-transparent text-red-600 shadow-none"
                                    type="button"
                                    onClick={() =>
                                      removeAssignment(rosterEntry.id)
                                    }
                                  >
                                    Remove
                                  </Button>
                                )}
                              </li>
                            );
                          },
                        )}
                      </ol>
                    </div>
                  );
                },
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
